import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEventSource } from '@hooks/useEventSource';
import { useAuthStore } from '@stores/auth';
import { tokenStorage } from '@lib/auth/storage';
import { portalJobsApi } from '../api';
import type { JobUpdateEvent } from '../types';

export interface UsePortalJobsStreamOptions {
  /** Disable the connection without unmounting. Default true. */
  enabled?: boolean;
}

/**
 * Application-wide subscription to the `job-update` SSE channel.
 *
 * Mount this once near the top of an authenticated layout — it invalidates
 * relevant React Query caches whenever ANY portal job updates, so every page
 * that reads `['portal-job', id]` or `['portal-jobs']` stays fresh without
 * each component opening its own SSE connection (browsers cap concurrent
 * EventSource connections per origin).
 */
export function usePortalJobsStream(options: UsePortalJobsStreamOptions = {}) {
  const { enabled = true } = options;
  const qc = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Track the access token so the stream reconnects after token rotation.
  const [token, setToken] = useState<string | null>(() => tokenStorage.getAccessToken());
  useEffect(() => {
    setToken(tokenStorage.getAccessToken());
    const id = setInterval(() => {
      const current = tokenStorage.getAccessToken();
      setToken((prev) => (prev === current ? prev : current));
    }, 30_000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  const url = enabled && isAuthenticated && token ? portalJobsApi.streamUrl('job-update') : null;

  const { status } = useEventSource(url, {
    enabled: enabled && isAuthenticated,
    connectionKey: token,
    onEvent: (_type, data) => {
      const evt = data as JobUpdateEvent | string | null;
      if (!evt || typeof evt !== 'object') return;
      const eventJobId = evt.jobId ?? evt._id;
      if (eventJobId) {
        void qc.invalidateQueries({ queryKey: ['portal-job', eventJobId] });
      }
      // Always invalidate the list — pagination and aggregate counts may shift
      // even when we don't recognize the specific job id.
      void qc.invalidateQueries({ queryKey: ['portal-jobs'] });
    },
  });

  return { status };
}
